import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronsUpDown,
  CircleHelp,
  Loader2,
  LogOut,
  ScrollText,
  UserCircle2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuthStore } from '@/stores/auth';
import { useHelpGuidesStore } from '@/stores/helpGuides';
import { useSystemDocumentsStore } from '@/stores/systemDocuments';
import { fetchMyAvatar } from '@/views/profile/services/profileService';

interface SidebarAccountMenuProps {
  sidebarCollapsed: boolean;
  showLabels: boolean;
}

export const SidebarAccountMenu = ({
  sidebarCollapsed,
  showLabels,
}: SidebarAccountMenuProps) => {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const openHelpGuide = useHelpGuidesStore((state) => state.openHelpGuide);
  const openLegalDocumentsDrawer = useSystemDocumentsStore(
    (state) => state.openLegalDocumentsDrawer,
  );
  const canAccessHelpGuides = !!user?.establishment_config_id;

  const userInitials = user?.name
    ? user.name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() || '')
        .join('')
    : 'U';

  const { data: avatarData } = useQuery({
    queryKey: ['sidebar-avatar', user?.id],
    queryFn: () => fetchMyAvatar(user?.id || ''),
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 2,
  });

  const isAvatarLoading = avatarData === undefined;
  const avatarUrl = avatarData?.url;

  return (
    <DropdownMenu open={accountMenuOpen} onOpenChange={setAccountMenuOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`w-full rounded-2xl transition ${
            accountMenuOpen
              ? 'border border-sidebar-border bg-sidebar-accent/30 hover:bg-sidebar-accent/50'
              : 'border border-transparent bg-transparent hover:bg-sidebar-accent/20'
          } ${
            sidebarCollapsed
              ? 'flex justify-center px-2 py-2.5'
              : 'flex items-center gap-3 px-2 py-2.5 text-left'
          }`}
          title={user?.name || 'Usuário'}
        >
          <Avatar className="h-10 w-10 rounded-xl">
            {isAvatarLoading ? (
              <div className="flex h-full w-full items-center justify-center rounded-xl bg-sidebar-accent/40">
                <Loader2 className="h-4 w-4 animate-spin text-sidebar-foreground/70" />
              </div>
            ) : avatarUrl ? (
              <AvatarImage
                src={avatarUrl}
                alt={user?.name || 'Usuário'}
                className="object-cover"
              />
            ) : (
              <AvatarFallback className="rounded-xl bg-sidebar-primary text-sidebar-primary-foreground font-semibold">
                {userInitials}
              </AvatarFallback>
            )}
          </Avatar>

          {!sidebarCollapsed && showLabels && (
            <>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-semibold text-sidebar-foreground">
                  {user?.name || 'Usuário'}
                </p>
                <p className="truncate text-[13px] text-sidebar-foreground/70">
                  {user?.email || '-'}
                </p>
              </div>
              <ChevronsUpDown className="h-4 w-4 shrink-0 text-sidebar-foreground/70" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        side="right"
        sideOffset={12}
        className="w-64 rounded-2xl border-border/70 p-2"
      >
        <div className="flex items-center gap-3 rounded-xl px-2 py-2">
          <Avatar className="h-10 w-10 rounded-xl">
            {isAvatarLoading ? (
              <div className="flex h-full w-full items-center justify-center rounded-xl bg-muted">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : avatarUrl ? (
              <AvatarImage
                src={avatarUrl}
                alt={user?.name || 'Usuário'}
                className="object-cover"
              />
            ) : (
              <AvatarFallback className="rounded-xl bg-primary text-primary-foreground font-semibold">
                {userInitials}
              </AvatarFallback>
            )}
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-[14px] font-semibold text-foreground">
              {user?.name || 'Usuário'}
            </p>
            <p className="truncate text-[12px] text-muted-foreground">
              {user?.email || '-'}
            </p>
          </div>
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="cursor-pointer gap-2 rounded-xl px-3 py-1.5 text-[14px]"
          onSelect={() => {
            window.location.href = '/profile';
          }}
        >
          <UserCircle2 className="h-4 w-4" />
          <span>{t('common.profile') || 'Perfil'}</span>
        </DropdownMenuItem>

        {canAccessHelpGuides && (
          <DropdownMenuItem
            className="cursor-pointer gap-2 rounded-xl px-3 py-1.5 text-[14px]"
            onSelect={() => openHelpGuide()}
          >
            <CircleHelp className="h-4 w-4" />
            <span>{t('sidebar.help_guide') || 'Guia de Ajuda'}</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          className="cursor-pointer gap-2 rounded-xl px-3 py-1.5 text-[14px]"
          onSelect={() => openLegalDocumentsDrawer()}
        >
          <ScrollText className="h-4 w-4" />
          <span>
            {t('system_documents.drawer_title') || 'Termos e Autorizações'}
          </span>
        </DropdownMenuItem>

        <DropdownMenuItem
          className="cursor-pointer gap-2 rounded-xl px-3 py-1.5 text-[14px] text-destructive focus:text-destructive"
          onSelect={logout}
        >
          <LogOut className="h-4 w-4" />
          <span>{t('common.logout') || 'Sair'}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
